import React from 'react';
import { useAuthStore } from '../../store';
import { evaluateClientPolicy } from '../../auth/policies.js';

interface CanProps {
  /** Single policy name */
  policy?: string;
  /** Multiple policy names (use with mode) */
  policies?: string[];
  /** 'all' = all policies must pass, 'any' = at least one (default: 'all') */
  mode?: 'all' | 'any';
  /** Fallback content when denied */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Conditionally renders children based on policy authorization.
 *
 * Usage:
 *   <Can policy="CanTrade"><TradingSection /></Can>
 *   <Can policies={["CanViewAgents", "CanManageWorkflows"]} mode="any">...</Can>
 */
export const Can: React.FC<CanProps> = ({ policy, policies, mode = 'all', fallback = null, children }) => {
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);

  let allowed: boolean;
  if (policy) {
    allowed = evaluateClientPolicy(policy, permissions, organizationPlan);
  } else if (policies && policies.length > 0) {
    const check =
      mode === 'any'
        ? policies.some((p) => evaluateClientPolicy(p, permissions, organizationPlan))
        : policies.every((p) => evaluateClientPolicy(p, permissions, organizationPlan));
    allowed = check;
  } else {
    allowed = true;
  }

  return <>{allowed ? children : fallback}</>;
};
